import { SimpleDeferred } from '@tableau/vizportalclient-common-components';
import { DialogProps, DialogType, DialogWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { DataConnectionErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Filter } from '~commonInfra/utils/Filter';
import { GeneratePublicKeyRequest } from '~src/authentication/serverApi/requests/GeneratePublicKeyRequest';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ConnectionAuthInfo, IDatabaseConnectionAuthInfo, IOAuthConnectionAuthInfo } from '~src/connectivity/serverApi/types/connection/IConnectionAuthInfo';
import { EncryptedConnectionKeychain, IOAuthKeychain, UnencryptedConnectionKeychain } from '~src/connectivity/serverApi/types/connection/IConnectionKeyChain';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { RevertFlowResult } from '~src/flow/serverApi/requests/RevertFlowRequest';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { IRevertWorkbookResult } from '~src/workbook/ServerApi/requests/RevertWorkbookRequest';

import { Messages } from '../../codegen/Localize';
import { OAuthCredentials } from '../../connectivity/resources/OAuthCredentials';
import { Flows } from '../../flow/resources/Flows';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { FlowRevision } from '../../revisionHistoryFlow/serverApi/types/FlowRevision';
import { IWorkbookRevision } from '../../revisionHistoryWorkbook/serverApi/types/IWorkbookRevision';
import { Workbooks } from '../../workbook/resources/Workbooks';
import {
  CheckDatabaseConnectionCredentialsDialog,
  CheckDatabaseConnectionCredentialsProps,
  CheckDatabaseConnectionCredentialsState} from '../components/CheckDatabaseConnectionCredentialsDialog';
import {
  CheckOAuthConnectionCredentialsDialog,
  CheckOAuthConnectionCredentialsProps} from '../components/CheckOAuthConnectionCredentialsDialog';
import { AddOAuthCredentialAction } from './AddOAuthCredentialAction';

const AddOAuthCredentialsOptionValue = 'new';

function showCheckCredentialsDialog(
  connection: ConnectionAuthInfo,
  revision: IWorkbookRevision | FlowRevision,
  authenticatedKeychains: UnencryptedConnectionKeychain[] = [],
  dialogsDeferred: SimpleDeferred<IRevertWorkbookResult | RevertFlowResult>
) {
  const dialogPromise = connection.isOAuth
    ? showOAuthConnectionDialog(
      <IOAuthConnectionAuthInfo>connection,
      revision,
      authenticatedKeychains,
      dialogsDeferred
    )
    : showDatabaseConnectionDialog(
      <IDatabaseConnectionAuthInfo>connection,
      revision,
      authenticatedKeychains,
      dialogsDeferred
    );
  dialogPromise.then((revertResult: IResult) => {
    // Pass any revert workbook error out to the revert workbook action handler
    if (ResultHelper.hasErrors(revertResult)) {
      dialogsDeferred.resolve(revertResult);
    }
  });
}

function showDatabaseConnectionDialog(
  connection: IDatabaseConnectionAuthInfo,
  revision: IWorkbookRevision | FlowRevision,
  authenticatedKeychains: UnencryptedConnectionKeychain[] = [],
  dialogsDeferred: SimpleDeferred<IRevertWorkbookResult | RevertFlowResult>
): Promise<IRevertWorkbookResult | RevertFlowResult> {
  let state = { connection: connection };
  state.connection.keychain.embedCredentials = true; // default embed credentials to true
  const props = {
    dialogTitle: Messages.revisionHistoryDatabaseCredentialsDialogTitle(),
    // if not a Workbook, it is a Flow
    dialogDescription: isWorkbookRevision(revision) ?
      Messages.revisionHistoryCredentialsDialogDatabaseDescriptionForWorkbook()
      : Messages.revisionHistoryCredentialsDialogDatabaseDescriptionForFlow()
  };

  const dialogInstance = openReactActionDialog<
    CheckDatabaseConnectionCredentialsProps,
    CheckDatabaseConnectionCredentialsState,
    IResult>({
      bodyComponent: CheckDatabaseConnectionCredentialsDialog,
      initialBodyProps: props,
      initialDialogState: state,
      actionName: Messages.signIn(),
      action: (_props, dialogState, updateDialogState) => {
        if (isWorkbookRevision(revision)) {
          return revertWorkbook(dialogState.connection.keychain, revision, authenticatedKeychains, dialogsDeferred)
            // check that the credentials are valid for the connection
            .then((revertWorkbookResult: IResult) => {
              if (ResultHelper.hasErrors(revertWorkbookResult) && revertWorkbookResult.errors[0].code === DataConnectionErrorCode.FailedConnectionCredentials) {
                updateDialogState({ errorMessage: Messages.signInFailedBadCredentials() });
                return Promise.reject(revertWorkbookResult);
              }
              // pass along the results
              return Promise.resolve(revertWorkbookResult);
            });
        } else if (isFlowRevision(revision)) {
          return revertFlow(dialogState.connection.keychain, revision, authenticatedKeychains, dialogsDeferred)
            .then((revertFlowResult: IResult) => {
              if (ResultHelper.hasErrors(revertFlowResult) && revertFlowResult.errors[0].code === DataConnectionErrorCode.FailedConnectionCredentials) {
                updateDialogState({ errorMessage: Messages.signInFailedBadCredentials() });
                return Promise.reject(revertFlowResult);
              }
              return Promise.resolve(revertFlowResult);
            });
        }
      },
      actionDisabled: (_props, state) =>
        !state.connection.keychain.username ||
        !state.connection.keychain.unencryptedPassword,
      dialogWidth: TabDialogWidth.width375px,
      dismissDialogOnActionFailed: false
    });

  dialogInstance.result.catch(() => dialogsDeferred.reject());

  return dialogInstance.result;
}

function getOAuthDialogContent(props: CheckOAuthConnectionCredentialsProps): JSX.Element {
  return React.createElement(CheckOAuthConnectionCredentialsDialog, props);
}

function getOAuthDialogWidget(dialogProps: DialogProps, oAuthProps: CheckOAuthConnectionCredentialsProps): JSX.Element {
  return React.createElement(DialogWidget, dialogProps, getOAuthDialogContent(oAuthProps));
}

function showOAuthConnectionDialog(
  connection: IOAuthConnectionAuthInfo,
  revision: IWorkbookRevision | FlowRevision,
  authenticatedKeychains: UnencryptedConnectionKeychain[] = [],
  dialogsDeferred: SimpleDeferred<IRevertWorkbookResult>
): Promise<IRevertWorkbookResult> {
  const signIn = (
    selectedOAuthCredentialsId: string,
    close: (result?: any) => void) => {
    const oAuthIdPromise = selectedOAuthCredentialsId
      ? Promise.resolve(selectedOAuthCredentialsId)
      : addOAuthCredential(connection);
    if (isWorkbookRevision(revision)) {
      return oAuthIdPromise.then((oAuthId: string) => {
        connection.keychain.oAuthKeyId = oAuthId;
        revertWorkbook(connection.keychain, revision, authenticatedKeychains, dialogsDeferred)
          .then((revertWorkbookResult: any) => {
            return close(revertWorkbookResult);
          });
      });
    } else if (isFlowRevision(revision)) {
      return oAuthIdPromise.then((oAuthId: string) => {
        connection.keychain.oAuthKeyId = oAuthId;
        revertFlow(connection.keychain, revision, authenticatedKeychains, dialogsDeferred)
          .then((revertFlowResult: any) => {
            return close(revertFlowResult);
          });
      });
    }
  };

  const onSelectionChange = (
    id: string,
    setSelectedId: (id: string) => void,
    setOAuthCredentials: (credentials: IOAuthCredential[]) => void) => {
    if (id === AddOAuthCredentialsOptionValue) {
      setSelectedId(undefined);

      addOAuthCredential(connection).then((credentialId: string) => {
        getOAuthCredentials(connection).then((oAuthCredentials: IOAuthCredential[]) => {
          setOAuthCredentials(oAuthCredentials);
          // Set the credentialId if it exists in the list
          if (_.find(oAuthCredentials, (credential: IOAuthCredential) => credential.id === credentialId))
            setSelectedId(credentialId);
        });
      });
    }
    else
      setSelectedId(id);
  };

  return getOAuthCredentials(connection).then(function(credentials: IOAuthCredential[]) {
    const modalInstance = ReactModalService.openCustom(closeAndDismiss => {
      const oAuthDialogProps: CheckOAuthConnectionCredentialsProps = {
        signIn: signIn,
        addOAuthCredentialsOptionText: Messages.addOAuthCredential({ CONNECTION_TYPE_NAME: connection.connectionTypeDisplayName }),
        addOAuthCredentialsOptionValue: AddOAuthCredentialsOptionValue,
        advisoryText: getOAuthAdvisoryText(connection),
        chooseCredentialsText: Messages.revisionHistoryOAuthChooseCredentialsLabel({
          TYPE_NAME: connection.connectionTypeDisplayName
        }),
        oAuthCredentials: credentials,
        selectedOAuthCredentialsId: '',
        // if not a Workbook, it is a Flow
        dialogDescription: isWorkbookRevision(revision) ?
          Messages.revisionHistoryCredentialsDialogOAuthDescriptionForWorkbook()
          : Messages.revisionHistoryCredentialsDialogOAuthDescriptionForFlow(),
        close: closeAndDismiss.close,
        onSelectionChange: onSelectionChange
      };

      let dialogProps = ReactModalService.getDefaultDialogProps({
        title: Messages.revisionHistoryOAuthCredentialsDialogTitle({
          TYPE_NAME: connection.connectionTypeDisplayName
        }),
        type: DialogType.NormalWithTitleBar,
        showCloseButton: true,
        shouldReturnFocusOnClose: true
      }, closeAndDismiss);
      dialogProps.onCloseRequest = closeAndDismiss.dismiss;
      dialogProps.handleGlassClick = closeAndDismiss.dismiss;

      return getOAuthDialogWidget(dialogProps, oAuthDialogProps);
    });
    modalInstance.result.catch(() => dialogsDeferred.reject());
    return modalInstance.result;
  });
}

function revertWorkbook(
  newKeychain: UnencryptedConnectionKeychain,
  revision: IWorkbookRevision,
  authenticatedKeychains: UnencryptedConnectionKeychain[],
  dialogsDeferred: SimpleDeferred<IRevertWorkbookResult>
): Promise<IRevertWorkbookResult> {
  return WebHttpClientWithErrorToastInstance.sendRequest(new GeneratePublicKeyRequest())
    .then(generatedPublicKey => {
      const unencryptedKeychains = _.clone(authenticatedKeychains);
      unencryptedKeychains.push(newKeychain);
      const encryptedKeychains = encryptKeychains(unencryptedKeychains, generatedPublicKey.key);

      return Workbooks.revertWorkbook(
        revision.workbookId,
        revision.versionNumber,
        encryptedKeychains,
        generatedPublicKey.keyId
      );
    })
    // check that the credentials are valid for the connection
    .then(revertWorkbookResult => {
      if (revertWorkbookResult && revertWorkbookResult.failedConnection) {
        // push on authenticated unencrypted keychain
        authenticatedKeychains.push(newKeychain);
        showCheckCredentialsDialog(revertWorkbookResult.failedConnection, revision, authenticatedKeychains, dialogsDeferred);
      } else if (!ResultHelper.hasErrors(revertWorkbookResult)) {
        dialogsDeferred.resolve(revertWorkbookResult);
      }
      // pass along the results
      return revertWorkbookResult;
    });
}

function revertFlow(
  newKeychain: UnencryptedConnectionKeychain,
  revision: FlowRevision,
  authenticatedKeychains: UnencryptedConnectionKeychain[],
  dialogsDeferred: SimpleDeferred<RevertFlowResult>
): Promise<RevertFlowResult> {
  return WebHttpClientWithErrorToastInstance.sendRequest(new GeneratePublicKeyRequest())
    .then(generatedPublicKey => {
      const unencryptedKeychains = _.clone(authenticatedKeychains);
      unencryptedKeychains.push(newKeychain);
      const encryptedKeychains = encryptKeychains(unencryptedKeychains, generatedPublicKey.key);

      return Flows.revertFlow(
        revision.flowId,
        revision.versionNumber,
        encryptedKeychains,
        generatedPublicKey.keyId
      );
    })
    // check that the credentials are valid for the connection
    .then(revertFlowResult => {
      if (revertFlowResult && revertFlowResult.failedConnection) {
        // push on authenticated unencrypted keychain
        authenticatedKeychains.push(newKeychain);
        showCheckCredentialsDialog(revertFlowResult.failedConnection, revision, authenticatedKeychains, dialogsDeferred);
      } else if (!ResultHelper.hasErrors(revertFlowResult)) {
        dialogsDeferred.resolve(revertFlowResult);
      }
      // pass along the results
      return revertFlowResult;
    });
}

function addOAuthCredential(connection: IOAuthConnectionAuthInfo) {
  return AddOAuthCredentialAction.attemptAuth(connection.connectionType);
}

function encryptKeychains(keychains: UnencryptedConnectionKeychain[], publicKey: rsa.IRSAPublicKey): EncryptedConnectionKeychain[] {
  return keychains.map(
    keychain => {
      if (isOAuthKeychain(keychain)) {
        return keychain;
      } else {
        const encryptedPassword = rsa.encrypt(keychain.unencryptedPassword, publicKey);
        return {
          connection: keychain.connection,
          username: keychain.username,
          authenticationMode: keychain.authenticationMode,
          embedCredentials: keychain.embedCredentials,
          encryptedPassword: encryptedPassword
        };
      }
    }
  );
}

function getOAuthAdvisoryText(connection: IOAuthConnectionAuthInfo) {
  const buttonText = Messages.signIn();
  const typeDisplayName = connection.connectionTypeDisplayName;
  return Messages.embeddedVizAuthAccountAdvisory({ BUTTON_HTML: buttonText, TYPE_NAME: typeDisplayName });
}

function getOAuthCredentials(connection: IOAuthConnectionAuthInfo) {
  const credentialsQuery = OAuthCredentials.allValid().addFilter(Filter.fieldEq('type', connection.connectionType));

  return SliceableHelper.fetchAll(credentialsQuery)
    .then(credentialsListResult => {
      return credentialsListResult.items;
    });
}

function isOAuthKeychain(keychain: UnencryptedConnectionKeychain): keychain is IOAuthKeychain {
  return (<IOAuthKeychain>keychain).oAuthKeyId !== undefined;
}

function isWorkbookRevision(revision: IWorkbookRevision | FlowRevision): revision is IWorkbookRevision {
  return (<IWorkbookRevision>revision).workbookId !== undefined;
}

function isFlowRevision(revision: IWorkbookRevision | FlowRevision): revision is FlowRevision {
  return (<FlowRevision>revision).flowId !== undefined;
}

export namespace CheckConnectionCredentialsAction {

  export function showCheckCredentialsDialogs(
    connection: ConnectionAuthInfo,
    revision: IWorkbookRevision | FlowRevision,
    authenticatedKeychains: UnencryptedConnectionKeychain[] = []
  ): Promise<IRevertWorkbookResult> {
    let dialogsDeferred;
    if (isWorkbookRevision(revision)) {
      dialogsDeferred = new SimpleDeferred<IRevertWorkbookResult>();
    }
    if (isFlowRevision(revision)) {
      dialogsDeferred = new SimpleDeferred<RevertFlowResult>();
    }

    showCheckCredentialsDialog(connection, revision, authenticatedKeychains, dialogsDeferred);

    return dialogsDeferred.promise;
  }
}

export type CheckConnectionCredentialsAction = typeof CheckConnectionCredentialsAction;
